<template>
  <div class="agent-window">
    <!-- 会话被后端移出服务队列（QU + SERVING + REMOVE）后强制切到错误页；
         复用 AgentAuthError 的极简全屏样式，跟 token 失效走同一出口。 -->
    <AgentAuthError v-if="cStore.agentSessionEnded" />
    <ChatDialog
      v-else-if="isLoad"
      :props="entranceProps"
      :dialog-style="dialogStyle"
      @close="handleClose"
    />
  </div>
</template>

<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script lang="ts">
/**
 * Purpose: 客服身份下渲染整页会话面板。相较 CustomerWindow：
 *   - 无入口按钮 / 无延时 / 无 localhost 分支：一进页面即挂载 ChatDialog；
 *   - chatId / userId / channelId 全部由入口 index.js 在挂载前从 URL 与 /userInfo 接口
 *     拉取后透传进来，AgentWindow 不再做任何身份发现（不像 CustomerWindow 要走
 *     getLatestChat / getUserHistory 的"我最近有什么会话"探测）；
 *   - 初始化立即调 getChatHistory 拉当前 chat 一页消息 + 打开 WS 订阅后续增量 +
 *     getUserHistory({userId}) 填充历史会话清单以支持跨会话翻历史。
 * Props: config.configId 是真实的 channelId；chatId 是当前会话 id；userId 是客户的 userId。
 */
import { defineComponent, onMounted, onUnmounted, ref } from 'vue'
import ChatDialog from './ChatDialog/ChatDialog.vue'
import AgentAuthError from './AgentAuthError.vue'
import { useChatStore } from '../../store/chat'
import { getChatHistory, getStyleSetting, getUserHistory } from '../../api/simple-api'
import {
  WIDGET_DIALOG_STYLE_CONSTRAINTS,
  normalizeWidgetDialogStyle,
} from '../../utils/widgetDialogStyle'
import { normalizeWidgetBrandStyle } from '../../utils/widgetBrandStyle'
import { ChatStatus, MessageType } from '../../enum/chatEnum'

interface Config {
  configId?: string
  styleSetting?: any | null
}

export default defineComponent({
  name: 'AgentWindow',
  components: { ChatDialog, AgentAuthError },
  props: {
    config: {
      type: Object as () => Config,
      required: true,
    },
    chatId: {
      type: String,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
    // 当前客服自己的 id（URL 里的 agentId，数字字符串）。
    // 用于消息气泡 isMe 判定，让别的客服（历史里被转接过的客服）消息落到左侧。
    agentId: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const cStore = useChatStore()
    const isLoad = ref(false)

    // 沿用访客侧的 DEFAULT_ENTRANCE_PROPS 结构，让 ChatDialog 拿到齐备的
    // announcementFlag / boxColor / logo / channelName 等字段，避免 undefined 造成的样式塌陷。
    // 入口按钮相关字段（entranceType/location/delay/buttonText）在客服整页模式下不生效，
    // 但结构上仍传全以避免 ChatDialog 内部访问未定义字段。
    const DEFAULT_ENTRANCE_PROPS = {
      id: '',
      channelId: '',
      channelName: '',
      remark: '',
      announcementFlag: false,
      announcement: {
        ['zh-CN']: '',
      },
      accessCode: '',
      linkCode: '',
      entranceType: 1,
      width: 44,
      height: 128,
      color: '#2e9b4a',
      location: 1,
      buttonText: {
        ['en-US']: 'Live Chat',
        ['zh-TW']: '在線客服',
        ['zh-CN']: '在线客服',
      },
      delay: '0',
      boxColor: '#2E9B4A',
      logo: '',
      agentLogo: '',
      boxWidth: WIDGET_DIALOG_STYLE_CONSTRAINTS.width.default,
      boxHeight: WIDGET_DIALOG_STYLE_CONSTRAINTS.height.default,
      borderRadius: WIDGET_DIALOG_STYLE_CONSTRAINTS.borderRadius.default,
      robotId: '',
    }

    const entranceProps = ref<any>(DEFAULT_ENTRANCE_PROPS)
    const dialogStyle = ref(normalizeWidgetDialogStyle(DEFAULT_ENTRANCE_PROPS))

    const mergeWithDefaults = (apiData: any) => {
      const result = { ...DEFAULT_ENTRANCE_PROPS } as any
      const source = apiData && typeof apiData === 'object' ? apiData : {}
      Object.keys(source).forEach((key) => {
        const value = source[key]
        if (value !== null && value !== undefined) {
          if (typeof value === 'object' && !Array.isArray(value)) {
            result[key] = { ...(result[key] || {}), ...value }
          } else {
            result[key] = value
          }
        }
      })
      return result
    }

    onMounted(async () => {
      // 身份/会话上下文一次性写入 store。index.js 已在挂载前把 role/memberId/agent token
      // 落到 channelStorage 模块级状态，chat store 首次实例化的 socket URL 已用正确
      // token+channelId 构造；此处仅是将结果物化到 store 供下游派生（isMe/头像视角/翻页）。
      cStore.role = 'agent'
      cStore.channelId = props.config.configId ?? '0'
      cStore.chatId = props.chatId
      cStore.userId = props.userId
      // 复用现有 currentAgent 字段承载"我是哪个客服"：访客视角下它是"我正在对话的客服"，
      // 客服视角下它是"我自己"，两种语义在下游的 isMe / isAgent 派生里都能对齐——
      // 消息气泡 isMe 通过 chat.senderId === cStore.currentAgent 判定，
      // 别的客服（历史里被转接过的其他客服）senderId 不匹配就会落到左侧。
      cStore.currentAgent = props.agentId
      cStore.chatStatus = ChatStatus.AGENT
      cStore.sendDisable = false

      // 样式配置：index.js 已经拉过一次并透传，这里正常复用；缺失时兜底再调一次。
      let styleSetting = props.config.styleSetting
      if (!styleSetting) {
        try {
          const response = await getStyleSetting(props.config.configId)
          const data = await response.json()
          styleSetting = data.data
        } catch (e) {
          console.warn('[AgentWindow] 样式配置获取失败，走默认值:', e)
        }
      }

      const mergedSettings = mergeWithDefaults(styleSetting)
      dialogStyle.value = normalizeWidgetDialogStyle(mergedSettings)
      const brandStyle = normalizeWidgetBrandStyle(mergedSettings.boxColor)
      entranceProps.value = {
        ...mergedSettings,
        boxColor: brandStyle.color,
        boxWidth: dialogStyle.value.width,
        boxHeight: dialogStyle.value.height,
        borderRadius: dialogStyle.value.borderRadius,
      }

      // 先建立 WS 订阅，再拉一页历史；两步都成功才 isLoad=true 展示。
      // 顺序上先 connect 是为了不错过在拉历史窗口期到达的实时增量消息。
      try {
        await cStore.wsConnect()
      } catch (e) {
        console.error('[AgentWindow] socket 建立失败:', e)
      }

      try {
        const res = await getChatHistory(props.chatId, undefined, props.config.configId)
        const body = await res.json()
        const list = body?.data ?? []

        // 会话结束兜底：运行时是靠 WS 的 QU+SERVING+REMOVE / ET 触发 agentSessionEnded 切
        // AgentAuthError；但刷新后 Pinia 被重置，WS 服务端不会主动补发结束事件，只能扫
        // 历史消息里是否已经存在 ET（eventTerminate）来回溯。命中即锁死 AgentAuthError，
        // 不挂 ChatDialog、不拉后续用户历史清单，避免误让客服以为还能继续接待。
        if (Array.isArray(list) && list.some((m: any) => m?.messageType === MessageType.ET)) {
          cStore.agentSessionEnded = true
          cStore.chatStatus = ChatStatus.TERMINATION
          cStore.sendDisable = true
          cStore.wsClose()
          isLoad.value = true
          return
        }

        // loadOldChat 内部已按 role='agent' 走客服过滤规则，直接喂即可。
        cStore.loadOldChat(list)

        // 分页游标初始化：让 CustomerContainer.loadMore 的跨会话翻历史链路能起作用。
        // - lastChatId = 当前 chatId：首轮上翻仍拉当前会话的更早消息；
        // - lastSequenceId = undefined：让 updateLastChatAndSequence 首次以"< 20 条"逻辑判尽头。
        cStore.lastChatId = props.chatId
        cStore.lastSequenceId = undefined
      } catch (e) {
        console.error('[AgentWindow] 历史消息拉取失败:', e)
      }

      // 拉当前客户的所有历史会话清单，供跨会话翻历史（chatHistory 由 findLastChatId 消费）。
      // simple-api 内部已按 role='agent' 走 /merchant/chats/chatHistory（主 SPA 路径）。
      // userId 由 index.js 从 /userInfo 拿到透传进来，不再从消息里抠。
      if (props.userId && props.userId !== '0') {
        try {
          const uhRes = await getUserHistory(props.config.configId, {
            userId: props.userId,
            validFlag: true,
          })
          const uhBody = await uhRes.json()
          cStore.chatHistory = (uhBody?.data ?? []).map((item: any) =>
            typeof item === 'string' ? item : item.chatId,
          )
        } catch (e) {
          console.warn('[AgentWindow] getUserHistory 失败，跨会话翻历史将不可用:', e)
        }
      }

      isLoad.value = true
    })

    // 移动浏览器切后台后 JS 被冻结、超时后 WS 也会被 OS 杀掉；服务端在此期间的推送与
    // 状态变化在前端本地极难做对齐。简化方案：切回前台时若发现 WS 已断，直接触发一次
    // location.reload() 让服务端做唯一的真实来源，跟"用户重新进了一次页面"等价。
    //
    // 桌面浏览器切页签也会 fire visibilitychange，但 WS 一直保活 —— 通过检查
    // cStore.wsIsConnected 把这个场景排除，避免每次切页签都刷新。
    //
    // 800ms 延时给 socket.io 检测连接状态的时间；1.5s 的 hidden 最小时长阈值排除极短
    // 抖动。会话已被后端结束（AgentAuthError 页）时不刷新，reload 后 onMounted 的 ET
    // 历史扫描依然会切回错误页，多一次网络往返没必要。
    const HIDDEN_MIN_MS = 1500
    let hiddenAt = 0
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      if (document.visibilityState !== 'visible') return
      const hiddenDuration = hiddenAt ? Date.now() - hiddenAt : 0
      hiddenAt = 0
      if (hiddenDuration < HIDDEN_MIN_MS) return
      if (cStore.agentSessionEnded) return
      setTimeout(() => {
        if (document.visibilityState !== 'visible') return
        if (cStore.agentSessionEnded) return
        if (!cStore.wsIsConnected) {
          window.location.reload()
        }
      }, 800)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    onUnmounted(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      cStore.wsClose()
    })

    const handleClose = () => {
      // 客服整页无关闭动作；ChatDialog header 关闭按钮触发时留空即可。
    }

    return { cStore, isLoad, entranceProps, dialogStyle, handleClose }
  },
})
</script>

<style lang="less" scoped>
.agent-window {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  z-index: 999;
}
</style>

